import {
  Component,
  createEffect,
  createMemo,
  For,
  JSXElement,
  Match,
  onCleanup,
  onMount,
  Show,
  Switch,
  useContext,
} from 'solid-js';
import {
  render,
} from 'solid-js/web';
import {
  AuthContext,
  AuthProvider,
  Breadcrumbs,
  DialogsPortal,
  Dropdown,
  FaIcon,
  Link,
  LocalApiContext,
  LocalApiProvider,
  LocalizationContext,
  LocalizationProvider,
  LocalizationSelector,
  Router,
  Routes,
  RoutingContext,
  ThemeProvider,
  ThemeSelector,
  useRoutingNavigate,
} from '../components';
import config from '../config';
import {
  AuthWall,
  Oauth,
} from '../dialogs/auth';
import * as localizations from '../localizations';
import { RouteHome } from './routes/home';
import { linkDocs, linkHome, linkLocal } from './routes/links';

const App: Component = () => {
  return (
    <ThemeProvider defaultTheme="system" themes={[
      'system',
      'light',
      'dark',
      'sakura',
    ]}>
      <LocalizationProvider dicts={localizations} defaultLang="en">
        <AuthProvider>
          <LocalApiProvider>
            <Router>
              <div class="main">
                <AppOauth>
                  <AppAuth>
                    <AppSheet />
                  </AppAuth>
                </AppOauth>
              </div>
              <FooterPanel />
              <DialogsPortal />
            </Router>
          </LocalApiProvider>
        </AuthProvider>
      </LocalizationProvider>
    </ThemeProvider>
  );
};

const AppOauth: Component<{
  children: JSXElement;
}> = (props) => {
  return (
    <Routes routes={[
      {
        path: 'oauth',
        component: () =>
          <Routes routes={[
            {
              component: Oauth,
            }
          ]} />,
      },
    ]} fallback={() => props.children} />
  );
};

const AppAuth: Component<{
  children: any;
}> = (props) => {
  const { status: authStatus, authRequired } = useContext(AuthContext)!;

  return (
    <Switch>
      <Match when={authStatus() == 'not_authenticated' && authRequired()}><AuthWall /></Match>
      <Match when={true}>{
        props.children
      }</Match>
    </Switch>
  );
};

const AppSheet: Component = () => {
  const { t } = useContext(LocalizationContext)!;
  const { session, cancelAuth } = useContext(AuthContext)!;
  const { connected } = useContext(LocalApiContext)!;
  const context = useContext(RoutingContext)!;
  const currentPage = createMemo(() => context.hierarchy().last());
  const navigate = useRoutingNavigate();

  const breadcrumbs = createMemo(() => {
    const breadcrumbs = context.hierarchy().toArray();
    let j = 0;
    for(let i = 0; i < breadcrumbs.length; ++i) {
      const breadcrumb = breadcrumbs[i]!;
      if(breadcrumb.breadcrumb == undefined) continue;
      if(breadcrumb.breadcrumbRoot) {
        j = 0;
      }
      if(j < i) {
        breadcrumbs[j] = breadcrumb;
      }
      ++j;
    }
    return breadcrumbs.slice(0, j);
  });

  const onHeaderMenu = (key: string) => {
    switch(key) {
    case 'profile':
      navigate('/me');
      break;
    case 'logout':
      cancelAuth();
      break;
    }
  };

  let metaDescription: HTMLMetaElement;
  onMount(() => {
    metaDescription = document.createElement('meta');
    metaDescription.name = 'description';
    document.head.append(metaDescription);
  });
  onCleanup(() => {
    metaDescription?.remove();
  });

  createEffect(() => {
    const pageTitle = currentPage()?.title();
    const appTitle = pageTitle ? t('app.title')(pageTitle) : t('app.name');
    document.querySelector('title')!.innerText = appTitle;
    metaDescription.content = appTitle;
  });
  return (
    <div class="sheet medium">
      <div class="logo"></div>
      <div class="ui">
        <div class="header">
          <div class="breadcrumbs-and-profile">
            <Breadcrumbs breadcrumbs={breadcrumbs().map((info) => info.breadcrumb).filter((breadcrumb) => !!breadcrumb)} />
            <Show when={connected()}>
              <Link class="local_app" href={linkLocal}><FaIcon regular hard-drive inline weak />{t('local_app.detected')}</Link>
            </Show>
            <Show when={session()?.userInfo}>
              <div class="buttons session">
                <Dropdown classList={{
                  align_end: true,
                }} title={session()?.userInfo?.title} items={[
                  {
                    key: 'profile',
                    title: t('user.profile'),
                  },
                  {
                    key: 'logout',
                    title: t('user.logout'),
                  },
                ]} onSelect={onHeaderMenu} alignEnd />
              </div>
            </Show>
          </div>
        </div>
      </div>
      {/* <nav class="ui sidebar">
        <For each={breadcrumbs().toReversed()}>{(info) =>
          <Show when={info.sidelinks?.()}>{(sidelinks) => <>
            <div class="section"><div>{sidelinks().header}</div></div>
            <div class="list">
              <For each={sidelinks().items}>{(item) =>
                item.link ? <Link href={item.link} class={item.selected ? 'highlighted' : undefined}><div>{item.title}</div></Link> : <div>{item.title}</div>
              }</For>
            </div>
          </>}</Show>
        }</For>
      </nav> */}
      <div class={`ui ${currentPage()?.uiClass ?? ''}`}>
        <RouteHome />
      </div>
    </div>
  );
};

const FooterPanel: Component = () => {
  const { t, lang } = useContext(LocalizationContext)!;

  return (
    <div class="footer_panel">
      <div class="buttons group">
        <ThemeSelector />
        <LocalizationSelector />
      </div>
      <div class="social_links">
        <div><a href={linkDocs(lang())}>{t('docs')}</a></div>
        <For each={config.socialLinks}>{(socialLink) =>
          <a href={socialLink.link} target="_blank">{t(`social_links.${socialLink.title}`)}</a>
        }</For>
      </div>
    </div>
  );
};

render(() => <App />, document.body);
